// Checkout recovery, host half — framework-free core of the leave guard so
// it can be unit-tested outside Angular. While the React messenger has an
// active checkout (it publishes the draft id on
// window.__pmreact_checkout_draft_id), a flagged USER leave — the header
// back or the hardware back button (the page flags both) — is held for the
// bundle's decision: it may answer with a recovery offer sheet, in which
// case the navigation is cancelled and replayed only if the customer
// declines. Browser back (popstate) is deliberately NOT handled here: the
// bundle absorbs it with a history sentinel, because a cancelled popstate
// leaves Ionic's outlet in a bad state. Programmatic navigations
// (add-credits, login, redirects) never engage, and every wait is bounded so
// a leave can never hang.

export const CHECKOUT_LEAVE_REQUEST_EVENT = 'pmreact:checkout_leave_requested';
export const CHECKOUT_LEAVE_DECIDED_EVENT = 'pmreact:checkout_leave_decided';
export const CHECKOUT_LEAVE_PROCEED_EVENT = 'pmreact:checkout_leave_proceed';
export const CHECKOUT_LEAVE_DECISION_TIMEOUT_MS = 2500;
// A flagged back that starts no navigation (e.g. hardware back at stack
// depth 1) must not arm the guard for a later programmatic navigation.
export const CHECKOUT_LEAVE_HEADER_BACK_TTL_MS = 1000;
const FALLBACK_LEAVE_URL = '/my-penmates';

export type CheckoutLeaveDeps = {
  now: () => number;
  target: EventTarget;
  activeDraftId: () => string | null;
  navigateBack: (url: string) => Promise<boolean>;
  decisionTimeoutMs?: number;
  headerBackTtlMs?: number;
};

export class CheckoutLeaveCoordinator {
  private headerBackAt: number | null = null;
  private bypassOnce = false;
  private pendingReplay: { requestId: string; nextUrl: string } | null = null;
  private counter = 0;

  constructor(private readonly deps: CheckoutLeaveDeps) {
    this.deps.target.addEventListener(CHECKOUT_LEAVE_PROCEED_EVENT, this.onProceed);
  }

  dispose(): void {
    this.deps.target.removeEventListener(CHECKOUT_LEAVE_PROCEED_EVENT, this.onProceed);
  }

  // The page's own back controls are imperative navigations; they flag
  // themselves so the guard can tell them from programmatic redirects.
  markHeaderBack(): void {
    this.headerBackAt = this.deps.now();
  }

  canLeave(nextUrl: string | null | undefined): boolean | Promise<boolean> {
    const ttl = this.deps.headerBackTtlMs != null ? this.deps.headerBackTtlMs : CHECKOUT_LEAVE_HEADER_BACK_TTL_MS;
    const headerBack = this.headerBackAt != null && this.deps.now() - this.headerBackAt < ttl;
    this.headerBackAt = null;
    if (this.bypassOnce) {
      this.bypassOnce = false;
      return true;
    }
    const draftId = this.deps.activeDraftId();
    if (!draftId) return true;
    if (!headerBack) return true;
    return this.askBundle(draftId, nextUrl || FALLBACK_LEAVE_URL);
  }

  private askBundle(draftId: string, nextUrl: string): Promise<boolean> {
    this.counter += 1;
    const requestId = `${this.deps.now()}-${this.counter}`;
    // Only the most recent request can be replayed.
    this.pendingReplay = null;
    const timeoutMs = this.deps.decisionTimeoutMs != null ? this.deps.decisionTimeoutMs : CHECKOUT_LEAVE_DECISION_TIMEOUT_MS;
    return new Promise<boolean>((resolve) => {
      let settled = false;
      let timer: any = null;
      const finish = (proceed: boolean) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        this.deps.target.removeEventListener(CHECKOUT_LEAVE_DECIDED_EVENT, onDecided);
        resolve(proceed);
      };
      const onDecided = (evt: Event) => {
        const detail = ((evt as CustomEvent).detail || {}) as any;
        if (String(detail.requestId || '') !== requestId) return;
        const proceed = !!detail.proceed;
        if (!proceed) this.pendingReplay = { requestId, nextUrl };
        finish(proceed);
      };
      this.deps.target.addEventListener(CHECKOUT_LEAVE_DECIDED_EVENT, onDecided);
      timer = setTimeout(() => finish(true), timeoutMs);
      try {
        this.deps.target.dispatchEvent(new CustomEvent(CHECKOUT_LEAVE_REQUEST_EVENT, { detail: { requestId, draftId } }));
      } catch (e) {
        finish(true);
      }
    });
  }

  // The customer declined the offer: the bundle has already left checkout;
  // replay the navigation they asked for, once, without asking again. A
  // replay that never reaches the guard (superseded, ignored) disarms itself.
  private onProceed = (evt: Event): void => {
    const detail = ((evt as CustomEvent).detail || {}) as any;
    const requestId = String(detail.requestId || '');
    const replay = this.pendingReplay;
    if (!replay || replay.requestId !== requestId) return;
    this.pendingReplay = null;
    this.bypassOnce = true;
    this.deps.navigateBack(replay.nextUrl).then(
      (ok) => {
        if (!ok) this.bypassOnce = false;
      },
      () => {
        this.bypassOnce = false;
      },
    );
  };
}
